import {
  type AppRole,
  AreaSchema,
  type EmployeeCreate,
  EmployeeSchema,
  type EmployeeUpdate,
  HierarchyNodeSchema,
  paginatedResponseSchema,
  type RevokeSessionsBody,
  RoleAssignmentSchema,
} from '@gestion-permisos/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useApi } from '../api/api-context.js';
import { ifMatch, useIntentKey, withIntent } from '../api/intent.js';
import { ApiProblem } from '../api/problem.js';
import { ADMIN_PAGE_SIZE } from './admin-api.js';

const EmployeePageSchema = paginatedResponseSchema(EmployeeSchema);
const AreaPageSchema = paginatedResponseSchema(AreaSchema);
const HierarchySchema = z.array(HierarchyNodeSchema);
const RoleListSchema = z.array(RoleAssignmentSchema);

/** Tamaño de las listas de los selectores (área y responsable): la API admite hasta 100 por página. */
export const OPTIONS_PAGE_SIZE = 100;

// ---------------------------------------------------------------- Empleados

export function useEmployeesPage(page: number) {
  const api = useApi();
  return useQuery({
    queryKey: ['admin', 'employees', 'page', page],
    queryFn: () => api.get(`/api/v1/employees?page=${page}&pageSize=${ADMIN_PAGE_SIZE}`, EmployeePageSchema),
  });
}

/**
 * Opciones del formulario: una página de áreas y una de empleados (responsables), de 100. Si `total` es
 * mayor, la pantalla lo indica; no se piden más páginas ni una petición por persona.
 */
export function useEmployeeFormOptions() {
  const api = useApi();
  const areas = useQuery({
    queryKey: ['admin', 'options', 'areas'],
    queryFn: () => api.get(`/api/v1/areas?pageSize=${OPTIONS_PAGE_SIZE}`, AreaPageSchema),
  });
  const managers = useEmployeeOptions();
  return { areas, managers };
}

/** Una página de 100 de `GET /employees` para un selector de personas (responsable en W8, aprobador en W9). */
export function useEmployeeOptions() {
  const api = useApi();
  return useQuery({
    queryKey: ['admin', 'options', 'employees'],
    queryFn: () => api.get(`/api/v1/employees?pageSize=${OPTIONS_PAGE_SIZE}`, EmployeePageSchema),
  });
}

export function useAdminEmployee(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['admin', 'employee', id],
    queryFn: () => api.get(`/api/v1/employees/${encodeURIComponent(id)}`, EmployeeSchema),
  });
}

function useAfterEmployeeChange() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'options', 'employees'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'hierarchy'] });
  };
}

/** `POST /employees` (sin `Idempotency-Key`). Las reglas (DG único, responsable, ciclos) las decide la API. */
export function useCreateEmployee() {
  const api = useApi();
  const after = useAfterEmployeeChange();
  return useMutation({
    mutationFn: (body: EmployeeCreate) => api.mutate('POST', '/api/v1/employees', body, { schema: EmployeeSchema }),
    onSuccess: after,
  });
}

/** `PATCH /employees/{id}` con `If-Match` y solo lo cambiado. Un 412 vuelve a leer `GET /employees/{id}`. */
export function useUpdateEmployee(id: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const after = useAfterEmployeeChange();
  return useMutation({
    mutationFn: ({ version, changes }: { version: number; changes: EmployeeUpdate }) =>
      api.mutate('PATCH', `/api/v1/employees/${encodeURIComponent(id)}`, changes, { schema: EmployeeSchema, headers: { 'If-Match': ifMatch(version) } }),
    onSuccess: (employee) => {
      queryClient.setQueryData(['admin', 'employee', id], employee);
      after();
    },
    onError: (error) => {
      if (error instanceof ApiProblem && error.status === 412) void queryClient.invalidateQueries({ queryKey: ['admin', 'employee', id] });
    },
  });
}

// ---------------------------------------------------------------- Jerarquía

/** Árbol en preorden con `depth` y los problemas que señala la API (el cliente no recorre ni calcula nada). */
export function useHierarchy() {
  const api = useApi();
  return useQuery({
    queryKey: ['admin', 'hierarchy'],
    queryFn: () => api.get('/api/v1/employees/hierarchy', HierarchySchema),
  });
}

// ---------------------------------------------------------------- Roles

export function useRoles(employeeId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['admin', 'roles', employeeId],
    queryFn: () => api.get(`/api/v1/employees/${encodeURIComponent(employeeId)}/roles`, RoleListSchema),
  });
}

function useAfterRoleChange(employeeId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'roles', employeeId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'hierarchy'] });
  };
}

/** `POST /employees/{id}/roles` con `{role}` e `Idempotency-Key` por intención (otro rol, otra clave). */
export function useAssignRole(employeeId: string) {
  const api = useApi();
  const intent = useIntentKey();
  const after = useAfterRoleChange(employeeId);
  return useMutation({
    mutationFn: (role: AppRole) =>
      withIntent(intent, { assign: employeeId, role }, (key) =>
        api.mutate('POST', `/api/v1/employees/${encodeURIComponent(employeeId)}/roles`, { role }, {
          schema: RoleAssignmentSchema,
          headers: { 'Idempotency-Key': key },
        }),
      ),
    onSuccess: after,
  });
}

/**
 * `POST /employees/{id}/roles/{role}/end` con `If-Match` de la versión de **esa asignación** e
 * `Idempotency-Key`. Un 409 (p. ej., `LAST_ADMINISTRATOR`) o un 412 vuelve a leer los roles.
 */
export function useEndRole(employeeId: string) {
  const api = useApi();
  const intent = useIntentKey();
  const after = useAfterRoleChange(employeeId);
  return useMutation({
    mutationFn: ({ role, version }: { role: AppRole; version: number }) =>
      withIntent(intent, { end: employeeId, role, version }, (key) =>
        api.mutate('POST', `/api/v1/employees/${encodeURIComponent(employeeId)}/roles/${role}/end`, undefined, {
          schema: RoleAssignmentSchema,
          headers: { 'If-Match': ifMatch(version), 'Idempotency-Key': key },
        }),
      ),
    onSuccess: after,
    onError: (error) => {
      if (error instanceof ApiProblem && (error.status === 409 || error.status === 412)) after();
    },
  });
}

// ---------------------------------------------------------------- Sesiones

/** `POST /admin/employees/{id}/sessions/revoke` (204, sin `Idempotency-Key`). El motivo no se registra aquí. */
export function useRevokeSessions(employeeId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (body: RevokeSessionsBody) => api.mutate('POST', `/api/v1/admin/employees/${encodeURIComponent(employeeId)}/sessions/revoke`, body),
  });
}
